/**
 * Simple country detection based on phone number patterns
 * @param {string} phoneNumber - Phone number in various formats
 * @returns {object} - Country info with flag and dial code
 */
export function getCountryFromPhone(phoneNumber) {
  if (!phoneNumber) {
    return { 
      flag: '🌍', 
      dialCode: '', 
      countryCode: '',
      name: 'Unknown'
    };
  }

  const cleaned = phoneNumber.replace(/\D/g, '');

  // Brazilian numbers (most common case)
  if (phoneNumber.includes('+55') || cleaned.startsWith('55') || cleaned.match(/^\d{10,11}$/)) {
    return {
      flag: '🇧🇷',
      dialCode: '+55',
      countryCode: 'BR',
      name: 'Brazil'
    };
  }

  // Other common countries
  if (phoneNumber.match(/^\+?1\d{10}/)) return { flag: '🇺🇸', dialCode: '+1', countryCode: 'US', name: 'United States' };
  if (phoneNumber.match(/^\+?44\d/)) return { flag: '🇬🇧', dialCode: '+44', countryCode: 'GB', name: 'United Kingdom' };
  if (phoneNumber.match(/^\+?33\d/)) return { flag: '🇫🇷', dialCode: '+33', countryCode: 'FR', name: 'France' };
  if (phoneNumber.match(/^\+?49\d/)) return { flag: '🇩🇪', dialCode: '+49', countryCode: 'DE', name: 'Germany' };
  if (phoneNumber.match(/^\+?34\d/)) return { flag: '🇪🇸', dialCode: '+34', countryCode: 'ES', name: 'Spain' };
  if (phoneNumber.match(/^\+?39\d/)) return { flag: '🇮🇹', dialCode: '+39', countryCode: 'IT', name: 'Italy' };
  
  return { 
    flag: '🌍', 
    dialCode: '', 
    countryCode: '',
    name: 'Unknown'
  };
}

/**
 * Format Brazilian phone number for display
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} - Formatted Brazilian phone number
 */
function formatBrazilianNumber(phoneNumber) {
  // Return the phone number as-is without any formatting
  return phoneNumber;
}

/**
 * Normalize Brazilian phone number - only clean formatting, don't add country codes
 * @param {string} phoneNumber - Phone number to normalize
 * @returns {string} - Cleaned phone number without formatting
 */
export function normalizeBrazilianNumber(phoneNumber) {
  if (!phoneNumber) return phoneNumber;
  
  // Only remove formatting characters (spaces, dashes, parentheses)
  // Keep the number as-is, including any country code if present
  const cleaned = phoneNumber.replace(/[\s\-\(\)]/g, '');
  
  // Return the cleaned number without adding any country code
  return cleaned;
}

/**
 * Format phone number for display with country flag
 * @param {string} phoneNumber - Phone number to format
 * @returns {object} - Formatted phone info with flag
 */
export function formatPhoneWithFlag(phoneNumber) {
  if (!phoneNumber) {
    return {
      formatted: 'Sem telefone',
      flag: '',
      country: null
    };
  }

  const country = getCountryFromPhone(phoneNumber);
  let formatted = phoneNumber;

  // Format based on country
  if (country.countryCode === 'BR') {
    formatted = formatBrazilianNumber(phoneNumber);
  } else {
    // For other countries, just clean up the display
    formatted = phoneNumber.replace(/^\+/, '');
  }

  return {
    formatted: formatted,
    flag: country.flag,
    country: country
  };
}

/**
 * Format phone number - only clean formatting, don't modify the number
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} - Cleaned phone number
 */
export function formatToE164(phoneNumber) {
  if (!phoneNumber) return '';

  // Only remove formatting characters, don't add country codes
  const cleaned = phoneNumber.replace(/[\s\-\(\)]/g, '');
  
  // Return the cleaned number as-is
  return cleaned;
}

/**
 * Extract phone number without country code
 * @param {string} phoneNumber - Phone number to extract from
 * @returns {string} - Phone number without country code
 */
export function extractPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';
  
  const cleaned = phoneNumber.replace(/\D/g, ''); // Remove all non-digits
  const countryInfo = getCountryFromPhone(phoneNumber);
  
  // Remove country code based on detected country
  switch (countryInfo.dialCode) {
    case '+55': // Brazil
      if (cleaned.startsWith('55') && cleaned.length >= 12) {
        return cleaned.substring(2);
      }
      break;
    case '+1': // USA/Canada
      if (cleaned.startsWith('1') && cleaned.length >= 11) {
        return cleaned.substring(1);
      }
      break;
    case '+44': // UK
      if (cleaned.startsWith('44')) {
        return cleaned.substring(2);
      }
      break;
    case '+33': // France
      if (cleaned.startsWith('33')) {
        return cleaned.substring(2);
      }
      break;
    case '+49': // Germany
      if (cleaned.startsWith('49')) {
        return cleaned.substring(2);
      }
      break;
    case '+34': // Spain
      if (cleaned.startsWith('34')) {
        return cleaned.substring(2);
      }
      break;
    case '+39': // Italy
      if (cleaned.startsWith('39')) {
        return cleaned.substring(2);
      }
      break;
  }
  
  // If no country code was removed, return the cleaned number
  return cleaned;
}